import { isEntity } from '@toggle/helpers';
import { EntitySearchType, groupedSubClassMap } from '@toggle/toggle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { assetSubClassCategories as categories } from '~/components/search-modal/assetSubClassCategories';
import { Chips } from '~/components/search-modal/components/chips/Chips';
import { ListItem } from '~/components/search-modal/components/list-item/ListItem';
import {
  addTitleAndSearchType,
  EntityOrThemeWithListTypes,
  isThemeItem,
  ListItemData,
} from '~/components/search-modal/utils';
import { SearchModalBase } from '~/components/search-modal-base/SearchModalBase';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useTagsSearchCriteria } from '~/hooks/use-tags-search/use-tags-search-criteria';
import { useTagsSearch } from '~/hooks/use-tags-search/useTagsSearch';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { useRecentList } from '~/views/turbo-chart/hooks/use-recent-list/useRecentList';

import { NoResults } from './components/NoResults';
import { useThemeSearch } from './hooks/use-theme-search/useThemeSearch';

//Hide economics pill
const assetSubClassCategories = categories.filter(c => c.value !== 'economics');

export const SearchAssetWidget = () => {
  const { t } = useTranslation('widget');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeChip, setActiveChip] = useState('');
  const currentQueryRef = useRef('');
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { trackAssetOpen, trackThemeOpen } = usePosthogEvents();

  const { themeSearchResults, handleTextChange } = useThemeSearch();

  const { navigateToPage } = useNavigateToAssetOverview();
  const navigate = useNavigate();
  const { recentlyOpenedThemesOrEntities } = useRecentList();
  const filteredRecentlyOpenedThemesOrEntities =
    recentlyOpenedThemesOrEntities.filter(themeOrEntityItem => {
      const isEntityItem = isEntity(themeOrEntityItem);

      if (isEntityItem) {
        return !isBasicUser || !themeOrEntityItem.gated;
      }

      return themeOrEntityItem?.version === 2;
    });

  const entityTabs = ['stocks', 'etfs', 'indexes', 'fx', 'commodities', 'govBonds'];
  const isEntityTab = entityTabs.includes(activeChip);
  const isAllTab = activeChip === '';
  const isThemesTab = activeChip === 'themes';
  const shouldUseLookup = isEntityTab && !currentQueryRef.current;

  const { criteria } = useTagsSearchCriteria({
    activeSubclassChip: activeChip,
  });
  const {
    tags: entitySearchResults,
    loading,
    query: searchQuery,
    searchTags,
  } = useTagsSearch({
    searchType: shouldUseLookup
      ? EntitySearchType.EntityLookup
      : EntitySearchType.EntityAndEconomies,
    params: {
      limit: 30,
      ...(shouldUseLookup ? criteria : {}),
    },
  });

  const filteredEntitySearchResults = useMemo(() => {
    if (isThemesTab) {
      return [];
    }

    if (isAllTab) {
      return entitySearchResults;
    }

    if (!searchQuery) {
      return entitySearchResults;
    }

    if (isEntityTab && activeChip in groupedSubClassMap) {
      const allowedSubClasses =
        groupedSubClassMap[activeChip as keyof typeof groupedSubClassMap];

      return entitySearchResults.filter(entity =>
        allowedSubClasses.includes(entity.sub_class)
      );
    }

    return entitySearchResults;
  }, [
    entitySearchResults,
    searchQuery,
    activeChip,
    isAllTab,
    isThemesTab,
    isEntityTab,
  ]);

  const showEntityResults = !!filteredEntitySearchResults.length;
  const showThemeResults = !!themeSearchResults.length;
  const isThemeChipActive = activeChip === 'themes';
  const isAllChipActive = activeChip === '';
  const themeSearchResultsIsVisible =
    showThemeResults &&
    (isThemeChipActive || (isAllChipActive && !!searchQuery));

  const entitiesTitle = isAllChipActive ? t('widget:search.assets') : '';
  const themesTitle =
    isAllChipActive && themeSearchResultsIsVisible
      ? t('widget:search.investmentThemes')
      : '';

  const searchListData: ListItemData[] = useMemo(() => {
    return [
      {
        data: filteredRecentlyOpenedThemesOrEntities,
        searchListType: 'recent',
        isVisible:
          !searchQuery &&
          !activeChip &&
          !!filteredRecentlyOpenedThemesOrEntities.length,
      },
      {
        title: entitiesTitle,
        data: filteredEntitySearchResults,
        searchListType: 'result',
        loading: loading && !showEntityResults,
        isVisible: loading || showEntityResults,
      },
      {
        title: themesTitle,
        data: themeSearchResults,
        searchListType: 'result',
        isVisible: themeSearchResultsIsVisible,
        isCollapsibleSection: showEntityResults,
      },
    ];
  }, [
    showEntityResults,
    filteredRecentlyOpenedThemesOrEntities,
    searchQuery,
    filteredEntitySearchResults,
    activeChip,
    showThemeResults,
    themeSearchResults,
  ]);

  const filteredData = useMemo(
    () =>
      searchListData
        .filter(e => e.isVisible)
        .flatMap(addTitleAndSearchType) as EntityOrThemeWithListTypes[],
    [searchListData]
  );

  useEffect(() => {
    setActiveChip(
      filteredRecentlyOpenedThemesOrEntities.length
        ? ''
        : assetSubClassCategories[1].value
    );
  }, [filteredRecentlyOpenedThemesOrEntities.length]);

  const onClose = () => {
    searchParams.delete(queryKeys.widget);
    setSearchParams(searchParams);
  };

  const handleSelectItem = (item: EntityOrThemeWithListTypes) => {
    const isTheme = isThemeItem(item);
    isTheme ? trackThemeOpen('search') : trackAssetOpen('search');
    isTheme
      ? navigate(`${appPaths.themeOverview}/${item.id}`)
      : navigateToPage({ entityTag: item.tag, searchParams });
  };

  const handleSearch = (value: string) => {
    currentQueryRef.current = value;
    searchTags(value);
    handleTextChange(value);
  };

  const handleChangeChip = (selectedChip: string) => {
    const newChip = selectedChip === activeChip ? '' : selectedChip;
    setActiveChip(newChip);
  };

  const getSearchAssetWidgetChipData = () => {
    const startIdx = filteredRecentlyOpenedThemesOrEntities.length ? 0 : 1;
    return assetSubClassCategories.slice(startIdx);
  };

  const chipData = getSearchAssetWidgetChipData();

  return (
    <SearchModalBase<EntityOrThemeWithListTypes>
      searchValue={searchQuery}
      value={searchQuery}
      handleSearch={handleSearch}
      selectItem={handleSelectItem}
      dropdownItems={filteredData}
      data-testid="search-asset-modal-widget"
      isOpen
      isLoading={loading}
      shouldResetScrollOnItemsChange={false}
      title={t('widget:search.dataSearchTitle')}
      onClose={onClose}
      searchInputVariant="secondary"
      showHeader={false}
      renderDropdownItem={(
        item,
        _isActive,
        isFocused,
        _isDisabled,
        setFocusedText
      ) => {
        return (
          <ListItem
            item={item}
            isFocused={isFocused}
            setFocusedText={setFocusedText}
            searchQuery={searchQuery}
            variant="secondary"
          />
        );
      }}
      beforeResultsNode={
        <Chips
          chipData={chipData}
          activeChip={activeChip}
          handleChangeChip={handleChangeChip}
          variant="secondary"
        />
      }
      placeholder={t('widget:search.placeholder')}
      noSearchResultsLabel={<NoResults />}
    />
  );
};
